import type { NavItem } from "@bruntworkwear-apps/ui";
import { icons } from "@bruntworkwear-apps/ui";
import { createElement } from "react";
import { FEEDBACK_NAV_ITEM } from "@/modules/feedback";
import type { AppConfig } from "@/shared/api";

/**
 * The application navigation, in the order the work is done.
 *
 * Icons are created here rather than in the shell so the design system stays
 * unaware of this application's routes.
 */
export const NAV_ITEMS: NavItem[] = [
  {
    label: "Overview",
    href: "/",
    icon: createElement(icons.Info, { size: 16, "aria-hidden": true }),
  },
  {
    label: "Run",
    href: "/run",
    icon: createElement(icons.Upload, { size: 16, "aria-hidden": true }),
  },
  {
    label: "Review",
    href: "/review",
    icon: createElement(icons.FileSpreadsheet, { size: 16, "aria-hidden": true }),
  },
  {
    label: "Reconciliation",
    href: "/reconciliation",
    icon: createElement(icons.Table2, { size: 16, "aria-hidden": true }),
  },
];

/**
 * The sidebar entries for a given server configuration.
 *
 * A function rather than a constant because the last entry is conditional, and
 * that condition is the whole of the logic here - which is why it lives outside
 * the component. `AppLayout` composes; this decides.
 *
 * **Optional all the way down, deliberately.** The type says `feedback` is
 * always present, and the type is describing an API that agrees with this
 * bundle. The bundle and the API are separately deployed artifacts, so a browser
 * can be holding a client that expects a field from a server that predates it -
 * and reading `config?.feedback.enabled` (the chain stopping one link short) took
 * the entire shell down with "Cannot read properties of undefined" against a dev
 * server started before the field existed. A config the client does not fully
 * recognize must cost one nav entry, never the application.
 *
 * @param config - The resolved `/api/config`, or undefined while it loads.
 * @returns The workflow steps, plus the report entry when reporting is live.
 */
export function navItemsFor(config: AppConfig | undefined): NavItem[] {
  return config?.feedback?.enabled ? [...NAV_ITEMS, FEEDBACK_NAV_ITEM] : NAV_ITEMS;
}
